'use client'

import React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { ChromaText } from '@/components/ui/chroma-text'
import { InsightThumb, dealInsightArt } from '@/components/insight-thumb'

export default function BlogSection() {
  const blogPosts = [
    {
      title: 'Welcome to Euclideum',
      description:
        'Discover how Euclideum is transforming the technology landscape with innovative solutions and cutting-edge platforms that empower businesses worldwide.',
      href: '/blogs/story',
    },
    {
      title: 'IntelQI UDC Platform',
      description:
        'Learn how our Unified Digital Campus platform enables seamless data integration across your enterprise, breaking down silos and enabling real-time insights.',
      href: '/products/udc',
    },
    {
      title: 'HRMS Solution',
      description:
        'Explore our comprehensive Human Resource Management System that streamlines workforce operations and transforms how you manage your most valuable asset.',
      href: '/products/hrms',
    },
  ]

  // One gradient per card, dealt so no two cards in the row share one; the
  // crops inside each mark still key off the card's own href.
  const arts = dealInsightArt(blogPosts.map((post) => post.href))

  return (
    <section className="bg-background pt-16 sm:pt-20 md:pt-28 pb-16 sm:pb-20 md:pb-28">
      <div className="container mx-auto">
        {/* ── Section header ── */}
        <div className="flex flex-col lg:flex-row justify-between items-start mb-8 sm:mb-10 md:mb-12">
          <div className="lg:max-w-2xl">
            <h2
              className={cn(
                'font-heading font-normal leading-tight m-0',
                'text-2xl sm:text-3xl md:text-5xl',
                'text-foreground'
              )}
            >
              <ChromaText>Explore Our Latest Insights</ChromaText>
            </h2>
            <p
              className={cn(
                'mt-3 sm:mt-4',
                'text-sm sm:text-base md:text-[18px]',
                'leading-relaxed',
                'text-[#6B7280]'
              )}
            >
              {/* FIX:  */}
              Stay updated with the latest trends, insights, and innovations
              from the world of technology and business solutions.
            </p>
          </div>

          <Link
            href="/blogs"
            className={cn(
              'inline-flex items-center gap-2 font-medium group transition-colors',
              'text-sm sm:text-base md:text-lg',
              // FIX: removed whitespace-nowrap globally only apply on lg+
              'lg:whitespace-nowrap',
              'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
              'text-foreground focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active',
              // Space above on mobile when stacked, none on lg row layout
              'mt-5 lg:mt-0 lg:shrink-0 lg:pt-1'
            )}
            aria-label="View all blogs"
          >
            <span className="relative inline-flex items-center gap-2">
              <span className="relative">
                View all blogs
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-600 dark:bg-primary-active transition-all duration-300 group-hover:w-full" />
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
                className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300 group-hover:translate-x-1 text-foreground"
              >
                <path
                  d="M6 4l8 8-8 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </Link>
        </div>

        {/* ── Blog cards grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {blogPosts.map((post, index) => (
            <Link
              key={index}
              href={post.href}
              className="group bg-card rounded-xl overflow-hidden border border-border hover:shadow-xl hover:border-emerald-600/30 dark:hover:border-primary-active/30 transition-all duration-300 capability-card"
            >
              {/* Media band: a 2x2 checker mark pinned flush to the card's
                  top-left corner (no inset), its filled cells one continuous
                  crop of a gradient artwork. The rest of the band is the card
                  surface. The band has no padding of its own so the mark's
                  outer edges coincide with the card's edges. */}
              <div className="relative h-40 sm:h-44 md:h-48">
                <div className="absolute left-0 top-0 h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32">
                  <InsightThumb seed={post.href} art={arts[index]} />
                </div>
              </div>

              {/* Card body. The band above already spaces the mark off the
                  title, so the body carries side and bottom padding only. */}
              <div className="px-3 pb-3 sm:px-4 sm:pb-4 md:px-5 md:pb-5">
                <h3 className="font-heading text-xl sm:text-2xl font-normal text-foreground mb-3 sm:mb-4">
                  {post.title}
                </h3>
                <p
                  className={cn(
                    'text-muted-foreground leading-relaxed mb-4 sm:mb-6',
                    'sm:min-h-[90px] md:min-h-[100px]',
                    'text-sm sm:text-base'
                  )}
                >
                  {post.description}
                </p>
                <div className="flex justify-end">
                  <span className="inline-block text-foreground font-semibold text-sm sm:text-base relative">
                    Read More
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-foreground transition-all duration-300 group-hover:w-full" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .capability-card { background: hsl(var(--muted)); }
            .capability-card:hover { background: linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--accent-green) / 0.12) 100%); }
            .dark .capability-card { background: hsl(var(--muted) / 0.6); }
            .dark .capability-card:hover { background: linear-gradient(135deg, hsl(var(--muted) / 0.6) 0%, hsl(var(--accent-green) / 0.12) 100%); }
          `,
        }}
      />
    </section>
  )
}
